import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { supabase } from '@/shared/lib/supabase'
import { useCurrentUser } from '@/features/auth/hooks/use-current-user'
import type { UserRole } from '@/shared/types'

export interface Invitation {
  id: string
  organization_id: string
  email: string
  role: UserRole
  invited_by: string
  token: string
  expires_at: string
  accepted_at: string | null
  created_at: string
  inviter?: {
    full_name: string | null
    email: string
  }
  teams: {
    id: string
    name: string
  }[]
}

export interface CreateInvitationData {
  email: string
  role: UserRole
  teamIds: string[]
}

async function fetchPendingInvitations(organizationId: string): Promise<Invitation[]> {
  const { data, error } = await supabase
    .from('invitations')
    .select(`
      *,
      inviter:users!invited_by(full_name, email)
    `)
    .eq('organization_id', organizationId)
    .is('accepted_at', null)
    .order('created_at', { ascending: false })

  if (error) {
    throw new Error(error.message)
  }

  if (!data || data.length === 0) {
    return []
  }

  // Fetch team assignments for all invitations
  const invitationIds = data.map(i => i.id)
  const { data: invitationTeams, error: teamsError } = await supabase
    .from('invitation_teams')
    .select(`
      invitation_id,
      team:teams(id, name)
    `)
    .in('invitation_id', invitationIds)

  if (teamsError) {
    throw new Error(teamsError.message)
  }

  // Group teams by invitation
  type TeamData = { id: string; name: string }
  const teamsByInvitation = new Map<string, TeamData[]>()
  for (const it of invitationTeams || []) {
    const invitationId = it.invitation_id
    if (!teamsByInvitation.has(invitationId)) {
      teamsByInvitation.set(invitationId, [])
    }
    if (it.team) {
      const team = it.team as unknown as TeamData
      teamsByInvitation.get(invitationId)!.push(team)
    }
  }

  return data.map(invitation => ({
    ...invitation,
    teams: teamsByInvitation.get(invitation.id) || []
  })) as Invitation[]
}

async function createInvitation(data: CreateInvitationData): Promise<Invitation> {
  const { data: invitation, error } = await supabase
    .rpc('create_invitation', {
      p_email: data.email,
      p_role: data.role,
      p_team_ids: data.teamIds
    })

  if (error) {
    // Handle specific error messages
    if (error.message.includes('already exists')) {
      throw new Error('USER_EXISTS')
    }
    throw new Error(error.message)
  }

  return invitation as Invitation
}

async function resendInvitation(invitationId: string): Promise<Invitation> {
  const { data: invitation, error } = await supabase
    .rpc('resend_invitation', {
      p_invitation_id: invitationId
    })

  if (error) {
    throw new Error(error.message)
  }

  return invitation as Invitation
}

async function cancelInvitation(invitationId: string): Promise<void> {
  const { error } = await supabase
    .rpc('cancel_invitation', {
      p_invitation_id: invitationId
    })

  if (error) {
    throw new Error(error.message)
  }
}

export function usePendingInvitations() {
  const { user } = useCurrentUser()

  return useQuery({
    queryKey: ['pendingInvitations', user?.organization_id],
    queryFn: () => fetchPendingInvitations(user!.organization_id!),
    enabled: !!user?.organization_id,
  })
}

export function useCreateInvitation() {
  const queryClient = useQueryClient()
  const { user } = useCurrentUser()

  return useMutation({
    mutationFn: createInvitation,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['pendingInvitations', user?.organization_id] })
    },
  })
}

export function useResendInvitation() {
  const queryClient = useQueryClient()
  const { user } = useCurrentUser()

  return useMutation({
    mutationFn: resendInvitation,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['pendingInvitations', user?.organization_id] })
    },
  })
}

export function useCancelInvitation() {
  const queryClient = useQueryClient()
  const { user } = useCurrentUser()

  return useMutation({
    mutationFn: cancelInvitation,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['pendingInvitations', user?.organization_id] })
    },
  })
}
